(() => {
  if (window.__ckCombinedSwitcherInit) return;
  window.__ckCombinedSwitcherInit = true;

  // Ensure utils is loaded
  if (typeof window.requireUtils === 'function') {
    window.requireUtils('combined-market-language-switcher.js');
  }

  if (!window.utils) {
    console.error('[combined-switcher] Utils not loaded!');
    return;
  }

  const ROOT = '[data-combined-market-language-switcher]';
  const ITEM = '[data-combined-switcher-item]';
  const TRIGGER = '[data-combined-switcher-trigger]';

  const parseShopifyUrl =
    window.utils?.parseShopifyUrl || (() => ({ market: null, lang: 'en', remainder: [] }));
  const buildShopifyUrl = window.utils?.buildShopifyUrl || (() => '/');
  const mapMarketHandleToUrlCode = window.utils?.mapMarketHandleToUrlCode || ((h) => h);

  /**
   * Get current display text from URL
   * Format: "Market Name (Language Name)"
   * Uses Shopify's localization data from meta tags or DOM if available
   */
  const getCurrentDisplay = () => {
    // Try to get from DOM (set by Liquid)
    const marketMeta = document.querySelector('meta[name="market-handle"]');
    const marketHandle = marketMeta ? marketMeta.getAttribute('content') : null;

    const parsed = parseShopifyUrl(window.location.pathname);
    const market = parsed.market || 'uk';
    const lang = parsed.lang || 'en';

    // Map market code to display name
    const marketNames = {
      uk: 'United Kingdom',
      eu: 'Europe',
      'european-union': 'Europe',
      us: 'United States',
      ca: 'Canada',
    };

    // Map language code to display name
    const langNames = {
      en: 'English',
      es: 'Español',
      fr: 'Français',
      de: 'Deutsch',
    };

    // Use market handle if available, otherwise use parsed market code
    let marketKey = marketHandle || market;
    marketKey = marketKey ? marketKey.toLowerCase() : 'uk';

    const marketName = marketNames[marketKey] || marketNames[market] || marketKey;
    const langName = langNames[lang] || lang;

    return `${marketName} (${langName})`;
  };

  /**
   * Update trigger text to show current market+language
   */
  const setTrigger = () => {
    const display = getCurrentDisplay();
    document.querySelectorAll(ROOT).forEach((root) => {
      const el = root.querySelector(TRIGGER);
      if (el) el.textContent = display;
    });
  };

  /**
   * Handle item click - navigate to selected market+language
   */
  document.addEventListener('click', (e) => {
    const item = e.target?.closest?.(ITEM);
    if (!item) return;

    const root = item.closest?.(ROOT);
    if (!root) return;

    root.dispatchEvent(new CustomEvent('ck-popover-close', { bubbles: true, composed: true }));

    // Get market code and language code directly from data attributes (set by Liquid)
    const marketCode = item.getAttribute('data-market-code');
    const languageCode = item.getAttribute('data-language-code') || 'en';

    if (!marketCode) {
      console.warn('[combined-switcher] Market code not available');
      return;
    }

    // Map market code to URL format (uk -> null, others -> as-is)
    const marketForUrl = marketCode === 'uk' ? null : marketCode;

    // Parse current URL to get remainder
    const parsed = parseShopifyUrl(window.location.pathname);

    // Build new URL
    const newUrl = buildShopifyUrl({
      lang: languageCode,
      market: marketForUrl,
      remainder: parsed.remainder || [],
    });

    // Store language preference
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('__ck_user_lang_preference', languageCode);
      } catch (e) {
        console.warn('[combined-switcher] Error storing language preference:', e);
      }
    }

    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem('__ck_target_lang', languageCode);
      } catch (e) {
        console.warn('[combined-switcher] Error storing target language:', e);
      }
    }

    // Trigger Weglot if available
    if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
      try {
        window.Weglot.switchTo(languageCode);
      } catch (e) {
        console.warn('[combined-switcher] Error triggering Weglot:', e);
      }
    }

    window.location.assign(newUrl);
  });

  /**
   * Check if country is Spanish-speaking and redirect to Spanish if needed
   * (Same logic as language-switcher.js)
   */
  const checkSpanishCountryRedirect = () => {
    let userLangPreference = null;
    if (typeof localStorage !== 'undefined') {
      try {
        userLangPreference = localStorage.getItem('__ck_user_lang_preference');
      } catch (e) {
        console.warn('[combined-switcher] Error reading language preference:', e);
      }
    }
    if (userLangPreference && userLangPreference !== 'es') {
      return;
    }

    const marketMeta = document.querySelector('meta[name="market-handle"]');
    const marketHandle = marketMeta ? marketMeta.getAttribute('content') : null;

    if (!marketHandle || marketHandle === 'uk') return;

    const spanishCountries = ['ES'];
    const countryMeta = document.querySelector('meta[name="country-code"]');
    const countryCode = countryMeta ? countryMeta.getAttribute('content') : null;

    if (!countryCode || !spanishCountries.includes(countryCode)) return;

    const parsed = parseShopifyUrl(window.location.pathname);
    if (parsed.lang !== 'en') return;

    const pathname = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;

    const shouldRedirect =
      pathname === '/' || pathname === '/en-eu' || pathname.startsWith('/en-eu/');

    if (!shouldRedirect) return;

    const redirectKey = `__ck_spanish_redirect_${pathname}`;
    let redirectChecked = null;
    if (typeof sessionStorage !== 'undefined') {
      try {
        redirectChecked = sessionStorage.getItem(redirectKey);
      } catch (e) {
        console.warn('[combined-switcher] Error reading redirect flag:', e);
      }
    }
    if (redirectChecked) return;

    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(redirectKey, 'true');
      } catch (e) {
        console.warn('[combined-switcher] Error setting redirect flag:', e);
      }
    }

    if (pathname === '/') {
      window.location.replace('/es-eu' + search + hash);
    } else if (pathname === '/en-eu') {
      window.location.replace('/es-eu' + search + hash);
    } else if (pathname.startsWith('/en-eu/')) {
      const newPath = pathname.replace('/en-eu/', '/es-eu/');
      window.location.replace(newPath + search + hash);
    }
  };

  /**
   * Filter out the currently selected option from dropdown (client-side backup)
   */
  const filterSelectedOption = () => {
    const parsed = parseShopifyUrl(window.location.pathname);
    const currentMarket = parsed.market || 'uk';
    const currentLang = parsed.lang || 'en';

    // Map market to code (handle 'gb' -> 'uk')
    let marketCode = currentMarket;
    if (currentMarket === 'gb' || currentMarket === 'uk' || !currentMarket) {
      marketCode = 'uk';
    }

    document.querySelectorAll(ITEM).forEach((item) => {
      const itemMarket = item.getAttribute('data-market-code');
      const itemLang = item.getAttribute('data-language-code') || 'en';

      // Hide if it matches current selection
      if (itemMarket === marketCode && itemLang === currentLang) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
      }
    });
  };

  /**
   * Sync trigger and Weglot on page load
   */
  const sync = () => {
    const parsed = parseShopifyUrl(window.location.pathname);
    const storedLang =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('__ck_target_lang') : null;
    const targetLang = storedLang || parsed.lang || 'en';

    if (storedLang && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('__ck_target_lang');
      } catch (e) {
        console.warn('[combined-switcher] Error removing stored language:', e);
      }
    }

    if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
      const currentWeglotLang = window.Weglot.getCurrentLang && window.Weglot.getCurrentLang();
      if (currentWeglotLang !== targetLang) {
        try {
          window.Weglot.switchTo(targetLang);
        } catch (e) {
          console.warn('[combined-switcher] Error triggering Weglot:', e);
        }
      }
    }

    setTrigger();
    filterSelectedOption();
  };

  const init = () => {
    checkSpanishCountryRedirect();

    const trySync = (attempts = 0) => {
      sync();

      const parsed = parseShopifyUrl(window.location.pathname);
      if (parsed.lang && window.Weglot && typeof window.Weglot.switchTo === 'function') {
        const currentWeglotLang = window.Weglot.getCurrentLang && window.Weglot.getCurrentLang();
        if (currentWeglotLang !== parsed.lang) {
          window.Weglot.switchTo(parsed.lang);
        }
      } else if (!parsed.market && window.Weglot && typeof window.Weglot.switchTo === 'function') {
        const currentWeglotLang = window.Weglot.getCurrentLang && window.Weglot.getCurrentLang();
        if (currentWeglotLang !== 'en') {
          window.Weglot.switchTo('en');
        }
      }

      if (!window.Weglot && attempts < 10) {
        setTimeout(() => trySync(attempts + 1), 100);
      }
    };

    trySync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('popstate', sync);
})();
